import { Platform } from 'react-native';

import { recordError, track } from './analytics';

// Lazy-loaded OTA-update module — same pattern as app/lib/ads.ts.
// babel-preset-expo inlines Platform.OS at build time, so on web this `if`
// becomes `if (false)` and the require() is dead-code eliminated before Metro
// bundles it (expo-updates is a native module with no web implementation).
type UpdatesModule = {
  // false in __DEV__, in Expo Go, and in any build made without the `updates`
  // config in app.json — the single guard that gates the whole flow.
  isEnabled: boolean;
  checkForUpdateAsync: () => Promise<{ isAvailable: boolean }>;
  fetchUpdateAsync: () => Promise<{ isNew: boolean }>;
  reloadAsync: () => Promise<void>;
};

let Updates: UpdatesModule | null = null;

if (Platform.OS !== 'web') {
  try {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    Updates = require('expo-updates');
  } catch (err) {
    if (__DEV__) console.warn('[updates] module unavailable:', err);
  }
}

let checking: Promise<boolean> | null = null;

/**
 * Checks the EAS Update server for a newer JS bundle for this build's
 * runtime version. If one is available it is downloaded and the app reloads
 * into it. Resolves to `true` when an update was applied (the app will be
 * reloading), `false` otherwise.
 *
 * Fire-and-forget safe: never throws — errors are reported to Crashlytics and
 * swallowed so a failed update check can never crash the app.
 *
 * No-ops (returns false) on web, in dev, in Expo Go, and in any build made
 * without the `updates` config — all of which surface as `isEnabled === false`.
 * Concurrent calls are deduplicated via the in-flight promise.
 */
export const checkForUpdatesAsync = async (): Promise<boolean> => {
  if (!Updates || !Updates.isEnabled) return false;
  if (checking) return checking;

  const updates = Updates;

  checking = (async () => {
    try {
      const { isAvailable } = await updates.checkForUpdateAsync();
      if (!isAvailable) return false;

      track('ota_update_available');
      await updates.fetchUpdateAsync();
      track('ota_update_applied');
      // Restarts the app into the freshly downloaded bundle. Code after this
      // line does not run once the reload takes effect.
      await updates.reloadAsync();
      return true;
    } catch (err) {
      recordError(err instanceof Error ? err : new Error(String(err)), 'updates:check');
      return false;
    } finally {
      checking = null;
    }
  })();

  return checking;
};

export default { checkForUpdatesAsync };
